import type { ApiClient } from './client';
import { UnauthorizedError } from './errors';
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import { overviewApi } from './overview-routes';
import { careShiftApi } from './care-shift-routes';
import { pulseApi } from './pulse-routes';
import { shellApi } from './shell-routes';
import type {
  BellResponse,
  LoginOutcome,
  LoginRequest,
  MarkAllReadResponse,
  MarkReadResponse,
  MeResponse,
  NavBadgesResponse,
  SessionGrant,
  TwofaChallengeRequest,
  TwofaEnrollStart,
  TwofaEnrollVerified,
} from './types';

/** Mount point of the Aithora router (src/server.ts: app.use('/api/aithora', ...)). */
export const AITHORA = AITHORA_PREFIX;

// ── Auth ────────────────────────────────────────────────────────────────────

const LOGIN_PATH = `${AITHORA}/auth/login`;

/**
 * POST /auth/login, turned into a closed set of outcomes. A 401 that asks for a second
 * factor, a password change or the older MFA code is the next step of signing in; a
 * 401 without any of those (wrong password) is thrown as UnauthorizedError.
 */
export async function login(client: ApiClient, body: LoginRequest): Promise<LoginOutcome> {
  const res = await client.request<Record<string, unknown>>('POST', LOGIN_PATH, {
    body,
    anonymous: true,
    acceptStatuses: [401],
  });
  const d = (res.data ?? {}) as Record<string, unknown>;
  if (res.status === 200 && typeof d.token === 'string') {
    return { kind: 'session', grant: d as unknown as SessionGrant };
  }
  if (d.twofaRequired === true && typeof d.pendingToken === 'string') {
    const factors = Array.isArray(d.availableFactors)
      ? (d.availableFactors as unknown[]).filter((f): f is 'passkey' | 'totp' => f === 'passkey' || f === 'totp')
      : [];
    return {
      kind: 'second_factor',
      mode: d.twofaMode === 'enroll' ? 'enroll' : 'challenge',
      factors,
      pendingToken: d.pendingToken,
      expiresAt: String(d.expiresAt ?? ''),
    };
  }
  if (d.passwordChangeRequired === true && typeof d.changeToken === 'string') {
    return { kind: 'password_change', changeToken: d.changeToken, expiresAt: String(d.expiresAt ?? '') };
  }
  if (d.mfaRequired === true || d.mfaEnrollmentRequired === true) {
    return {
      kind: 'legacy_mfa',
      message: typeof d.error === 'string' ? d.error : 'Enter the code from your authenticator app.',
      enrollment:
        d.mfaEnrollmentRequired === true && typeof d.secret === 'string' && typeof d.otpauth === 'string'
          ? { secret: d.secret, otpauth: d.otpauth }
          : null,
    };
  }
  throw new UnauthorizedError({
    kind: 'unauthorized',
    status: res.status,
    message: typeof d.error === 'string' ? d.error : 'Sign-in failed.',
    requestId: res.requestId,
    body: d,
    method: 'POST',
    path: LOGIN_PATH,
  });
}

/** POST /2fa/challenge: a TOTP code or a single-use backup code. */
export const twofaChallenge = route<SessionGrant, void, TwofaChallengeRequest>()('POST', `${AITHORA}/2fa/challenge`, { anonymous: true });

/** POST /2fa/passkey/auth-options: WebAuthn request options (JSON, base64url). */
export const passkeyAuthOptions = route<PublicKeyRequestOptionsJson, void, { pendingToken: string }>()(
  'POST', `${AITHORA}/2fa/passkey/auth-options`, { anonymous: true });

/** POST /2fa/passkey/auth-verify: the signed assertion. */
export const passkeyAuthVerify = route<SessionGrant, void, { pendingToken: string; response: unknown; trustDevice?: boolean; deviceName?: string }>()(
  'POST', `${AITHORA}/2fa/passkey/auth-verify`, { anonymous: true });

/** POST /2fa/enroll with a mode=enroll pending token: a new TOTP secret and backup codes. */
export const twofaEnroll = route<TwofaEnrollStart, void, { pendingToken: string }>()('POST', `${AITHORA}/2fa/enroll`, { anonymous: true });

/** POST /2fa/verify-enrollment: the first code; returns the session when bootstrapped. */
export const twofaVerifyEnrollment = route<TwofaEnrollVerified, void, { pendingToken: string; code: string }>()(
  'POST', `${AITHORA}/2fa/verify-enrollment`, { anonymous: true });

/** POST /auth/change-password with the change token from login. */
export const changePassword = route<SessionGrant, void, { changeToken: string; newPassword: string }>()(
  'POST', `${AITHORA}/auth/change-password`, { anonymous: true });

/** GET /auth/me: who the session belongs to, with the organisation name. */
export const me = route<MeResponse>()('GET', `${AITHORA}/auth/me`);

/** PATCH /auth/profile {lang}: save the language this person picked, to their account.
 *  Not sent while viewing as another role (the server refuses it there too). */
export const profileLangSave = route<{ ok: boolean; lang: 'en' | 'es' }, void, { lang: 'en' | 'es' }>()('PATCH', `${AITHORA}/auth/profile`);
/** PATCH /auth/profile {tutorialSeen: true}: the "First time here" banner was shown or dismissed. */
export const profileTutorialSeen = route<{ ok: boolean; tutorialSeen: true }, void, { tutorialSeen: true }>()('PATCH', `${AITHORA}/auth/profile`);

/** POST /auth/logout: ends the session server-side. */
export const logout = route<{ ok: boolean }>()('POST', `${AITHORA}/auth/logout`, { readOnlySafe: true });
/** POST /auth/logout {refreshToken}: ends the session and this phone's Face ID sign-in. */
export const logoutDevice = route<{ ok: boolean }, void, { refreshToken: string }>()('POST', `${AITHORA}/auth/logout`, { readOnlySafe: true });
/** POST /auth/logout {everywhere: true}: ends every session and every enrolled phone. */
export const signOutEverywhere = route<{ ok: boolean }, void, { everywhere: true }>()('POST', `${AITHORA}/auth/logout`, { readOnlySafe: true });

/** A phone enrolled for Face ID sign-in (router.ts /auth/device-refresh/enroll). */
export interface DeviceRefreshEnrolled {
  ok: true;
  /** The trusted_devices row: it shows in, and is revoked from, the trusted-devices list. */
  deviceId: string;
  deviceExpiresAt: string;
  /** Keep in the phone's secure store; single use, the next one comes back each refresh. */
  refreshToken: string;
  expiresAt: string;
}
/** POST /auth/device-refresh/enroll {deviceName?, password? | code?}: from a session made in
 *  the last 15 minutes, or with the password or a code again (401 reauthRequired). */
export const deviceRefreshEnroll = route<DeviceRefreshEnrolled, void, { deviceName?: string; password?: string; code?: string }>()(
  'POST', `${AITHORA}/auth/device-refresh/enroll`);

/** A new session from the phone's refresh token, with the next refresh token. */
export interface DeviceRefreshGrant {
  ok: true;
  userId: string;
  name: string | null;
  role: string;
  organizationId: string | null;
  token: string;
  expiresAt: string;
  refreshToken: string;
  refreshExpiresAt: string;
}
/** POST /auth/device-refresh {refreshToken}: after Face ID, when the session has ended. A
 *  token used twice revokes the phone (401 reason 'reused'); a suspended account is 403. */
export const deviceRefresh = route<DeviceRefreshGrant, void, { refreshToken: string }>()(
  'POST', `${AITHORA}/auth/device-refresh`, { anonymous: true });

// ── Shell ───────────────────────────────────────────────────────────────────

/** GET /nav/badges: the side-menu status labels for the session's role and scope. */
export const navBadges = route<NavBadgesResponse>()('GET', `${AITHORA}/nav/badges`, { honest: true });

/** GET /notifications?view=bell: the alerts bell. */
export const bell = route<BellResponse, { view: 'bell'; limit?: number; offset?: number; status?: string }>()(
  'GET', `${AITHORA}/notifications`, { honest: true });

/** POST /notifications/:id/read: opening a row marks it read for this user. */
export const markRead = route<MarkReadResponse>()('POST', `${AITHORA}/notifications/:id/read`, { honest: true });

/** POST /notifications/read-all: every alert in scope, for this user. */
export const markAllRead = route<MarkAllReadResponse>()('POST', `${AITHORA}/notifications/read-all`, { honest: true });

// ── WebAuthn JSON (the shape @simplewebauthn/server emits) ──────────────────

export interface PublicKeyRequestOptionsJson {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: { id: string; type: 'public-key'; transports?: string[] }[];
  userVerification?: 'required' | 'preferred' | 'discouraged';
  extensions?: Record<string, unknown>;
}

/** Every route in one object, for callers that prefer `api.navBadges(client)`. */
export const api = {
  login,
  twofaChallenge,
  passkeyAuthOptions,
  passkeyAuthVerify,
  twofaEnroll,
  twofaVerifyEnrollment,
  changePassword,
  me,
  profileLangSave,
  profileTutorialSeen,
  logout,
  logoutDevice,
  signOutEverywhere,
  deviceRefreshEnroll,
  deviceRefresh,
  navBadges,
  bell,
  markRead,
  markAllRead,
  ...overviewApi,
  ...careShiftApi,
  ...pulseApi,
  ...shellApi,
} as const;
